import { Injectable, signal } from '@angular/core';
import { OnlyAviaFare } from '@appTypes/api.types';

/** The fare the cart held (qa.md R-FP35): its package id and code - the code survives a new search, the package id may not. */
export type AviaReturnFare = Pick<OnlyAviaFare, 'packageId' | 'code'>;

/**
 * Flight fare picker (qa.md R-FH16 "Choose another fare"): the checkout drops the flight from the cart and goes back to the avia
 * results; the row of the same flight (same flight numbers - a new search gives new package ids) opens its details again so the
 * agent picks another fare. R-FP35: the details open on the fare the cart held (FLEX), not on the cheapest. Root-provided: it carries
 * one flight key and that flight's fare across the page change, nothing else.
 */
@Injectable({ providedIn: 'root' })
export class AviaFareReturnService {
  readonly #flightKey = signal<string | null>(null);
  #fare: { flightKey: string; fare: AviaReturnFare } | null = null;

  /** Remembers the flight whose details should open on the results page, and the fare its cart item held (R-FP35). */
  requestReopen(flightKey: string, fare?: AviaReturnFare | null): void {
    this.#flightKey.set(flightKey || null);
    this.#fare = flightKey && fare ? { flightKey, fare } : null;
  }

  /** true once for the row of that flight; the request is cleared so the details open only once. */
  consume(flightKey: string): boolean {
    if (!flightKey || this.#flightKey() !== flightKey) return false;
    this.#flightKey.set(null);
    return true;
  }

  /** R-FP35: the cart's fare of that flight, once - the reopened details select it; null for any other flight. */
  takeFare(flightKey: string): AviaReturnFare | null {
    if (!flightKey || this.#fare?.flightKey !== flightKey) return null;
    const { fare } = this.#fare;
    this.#fare = null;
    return fare;
  }
}
